import { readEnv } from '@/env';

/**
 * Resolve a BE-returned file path against the API base so a server-relative
 * `filePath` (e.g. `/files/foo.pdf`) lands behind the BFF proxy /
 * `VITE_API_BASE_URL` rather than the current page origin. Absolute
 * `http(s)://…` URLs and blob URLs pass through.
 */
function resolveDownloadUrl(filePath: string): string {
  if (/^(https?:|blob:)/i.test(filePath)) return filePath;
  const base = readEnv().apiBaseUrl.replace(/\/+$/, '');
  const resolvedBase =
    base.startsWith('/') && typeof window !== 'undefined'
      ? `${window.location.origin}${base}`
      : base;
  const path = filePath.startsWith('/') ? filePath : `/${filePath}`;
  return `${resolvedBase}${path}`;
}

/**
 * Fetch a BE attachment as a `Blob` with the BFF session cookie attached.
 * The preview and download paths both use this — they only diverge on what
 * they do with the resulting blob (render inline vs trigger save-as).
 */
export async function fetchAttachmentBlob(
  filePath: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; contentType: string | null }> {
  const url = resolveDownloadUrl(filePath);
  const response = await fetch(url, {
    credentials: 'include',
    mode: 'cors',
    ...(signal !== undefined ? { signal } : {}),
  });
  if (!response.ok) {
    throw new Error(`Attachment fetch failed: HTTP ${String(response.status)}`);
  }
  const blob = await response.blob();
  return { blob, contentType: response.headers.get('content-type') };
}

/**
 * Fetch a file from the BFF (cookie attached) and trigger a save-as. The
 * BFF cookie only goes out when we explicitly request `credentials: 'include'`,
 * so we can't drop in a bare `<a href download>` link.
 */
export async function downloadBlob(
  filePath: string,
  filename: string,
): Promise<void> {
  const { blob } = await fetchAttachmentBlob(filePath);
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

